import { createFileRoute } from '@tanstack/react-router';
import { Award, Info, TrendingDown, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalysisSwitcher } from '@/components/AnalysisSwitcher';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';
import { AppShell } from '@/components/layout/AppShell';
import { Card, EmptyState, Skeleton } from '@/components/ui/primitives';
import { cx, formatAmount, formatFullDate, formatPercent, formatRate } from '@/lib/format';
import { useTiming } from '@/lib/queries';
import { useSelectedCurrency } from '@/lib/useSelectedCurrency';

export const Route = createFileRoute('/app/timing')({ component: Timing });

/**
 * The timing review.
 *
 * This screen is what makes "analytics platform" an earned description rather
 * than a self-applied one. It answers a question a spreadsheet cannot: not
 * "what did I pay", but "was paying that any good".
 *
 * Two benchmarks, deliberately. The market TWAP says whether the entries landed
 * on cheap days. The DCA benchmark is the harder one - it is what doing nothing
 * at all would have achieved, and because fixed spending automatically buys more
 * when the rate is low, beating it is the only result that says the deliberation
 * was worth anything.
 */
function Timing() {
  const { t, i18n } = useTranslation();
  const { quote, select, available, isLoading } = useSelectedCurrency();
  const { data, isLoading: timingLoading } = useTiming(quote ?? undefined);
  const [explain, setExplain] = useState<'twap' | 'dca' | null>(null);

  const analysis = data?.analysis ?? null;
  const base = 'CNY';

  return (
    <AppShell
      title={t('nav.analysis')}
      toolbar={<CurrencySwitcher available={available} value={quote} onChange={select} />}
      subnavigation={<AnalysisSwitcher />}
      {...(quote ? { addDefaults: { quoteCurrency: quote } } : {})}
    >
      <div className="flex flex-col gap-4">
        <div className="lg:hidden">
          <CurrencySwitcher available={available} value={quote} onChange={select} />
        </div>

        {isLoading || timingLoading ? (
          <>
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-56 w-full" />
          </>
        ) : !analysis ? (
          <EmptyState
            icon={<TrendingUp size={40} strokeWidth={1.5} />}
            title={t('ledger.empty')}
            body={t('ledger.emptyHint')}
          />
        ) : analysis.marketTwapRate === null ? (
          <EmptyState
            icon={<TrendingUp size={40} strokeWidth={1.5} />}
            title={t('timing.noHistory')}
          />
        ) : (
          <>
            <ScoreCard
              edgePct={analysis.edgePct}
              savedBase={analysis.savedVsTwapBase}
              baseCurrency={base}
            />

            <Card>
              <div className="flex flex-col gap-4">
                <Benchmark
                  label={t('timing.yourAverage')}
                  value={formatRate(analysis.yourAvgRate)}
                  emphasis
                />
                <Benchmark
                  label={t('timing.marketTwap')}
                  value={formatRate(analysis.marketTwapRate)}
                  delta={analysis.edgePct}
                  onExplain={() => setExplain(explain === 'twap' ? null : 'twap')}
                  explaining={explain === 'twap'}
                  explanation={t('timing.explainTwap')}
                />
                <Benchmark
                  label={t('timing.dcaBenchmark')}
                  value={formatRate(analysis.dcaBenchmarkRate)}
                  delta={analysis.edgeVsDcaPct}
                  onExplain={() => setExplain(explain === 'dca' ? null : 'dca')}
                  explaining={explain === 'dca'}
                  explanation={t('timing.explainDca')}
                />
              </div>

              {analysis.windowFrom && analysis.windowTo && (
                <p className="tnum mt-4 border-t border-separator pt-3 text-[13px] text-tertiary">
                  {formatFullDate(analysis.windowFrom, i18n.language)} —{' '}
                  {formatFullDate(analysis.windowTo, i18n.language)} · {analysis.buyCount}{' '}
                  {t('position.txCount')}
                </p>
              )}
            </Card>

            <div className="grid gap-4 sm:grid-cols-2">
              {analysis.bestTx && (
                <TradeCard
                  kind="best"
                  title={t('timing.bestTrade')}
                  occurredOn={analysis.bestTx.occurredOn}
                  rate={analysis.bestTx.rate}
                  quoteAmount={analysis.bestTx.quoteAmount}
                  savedBase={analysis.bestTx.savedVsTwapBase}
                  quoteCurrency={quote ?? ''}
                  baseCurrency={base}
                />
              )}
              {analysis.worstTx && (
                <TradeCard
                  kind="worst"
                  title={t('timing.worstTrade')}
                  occurredOn={analysis.worstTx.occurredOn}
                  rate={analysis.worstTx.rate}
                  quoteAmount={analysis.worstTx.quoteAmount}
                  savedBase={analysis.worstTx.savedVsTwapBase}
                  quoteCurrency={quote ?? ''}
                  baseCurrency={base}
                />
              )}
            </div>

            {data!.scenarios.length > 0 && (
              <Card>
                <h3 className="text-[15px] font-semibold">{t('timing.scenarios')}</h3>
                <p className="mt-1 text-[13px] text-secondary">
                  每笔保持你自己的点差不变，只把市场那一腿平移——所以变化反映的是时机，不是执行价格好坏。
                </p>
                <ul className="mt-4 flex flex-col gap-3">
                  {data!.scenarios.map((s) => {
                    const worse = Number(s.baseCostDelta) > 0;
                    return (
                      <li key={s.shiftDays} className="flex items-baseline justify-between gap-3">
                        <span className="text-[15px] text-secondary">
                          {s.shiftDays > 0
                            ? t('timing.scenarioLater', { days: s.shiftDays })
                            : t('timing.scenarioEarlier', { days: Math.abs(s.shiftDays) })}
                        </span>
                        <span
                          className={cx(
                            'tnum shrink-0 text-[15px] font-semibold',
                            worse ? 'text-[var(--negative)]' : 'text-[var(--positive)]',
                          )}
                        >
                          {worse ? '+' : '−'}
                          {formatAmount(Math.abs(Number(s.baseCostDelta)), base)}{' '}
                          <span className="font-normal text-secondary">
                            ({formatPercent(s.ratePctDelta)})
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function ScoreCard({
  edgePct,
  savedBase,
  baseCurrency,
}: {
  edgePct: string | null;
  savedBase: string | null;
  baseCurrency: string;
}) {
  const { t } = useTranslation();
  const value = edgePct === null ? 0 : Number(edgePct);
  const ahead = value > 0.25;
  const behind = value < -0.25;

  return (
    <Card className="relative overflow-hidden">
      <div
        aria-hidden
        className={cx(
          'pointer-events-none absolute inset-0 opacity-[0.07]',
          ahead && 'bg-[var(--positive)]',
          behind && 'bg-[var(--negative)]',
        )}
      />
      <div className="relative flex items-center gap-4">
        <span
          aria-hidden
          className={cx(
            'flex size-14 shrink-0 items-center justify-center rounded-full',
            ahead && 'bg-[var(--positive)]/15 text-[var(--positive)]',
            behind && 'bg-[var(--negative)]/15 text-[var(--negative)]',
            !ahead && !behind && 'bg-[var(--fill-3)] text-secondary',
          )}
        >
          {ahead ? (
            <TrendingUp size={26} />
          ) : behind ? (
            <TrendingDown size={26} />
          ) : (
            <Award size={26} />
          )}
        </span>

        <div className="min-w-0">
          <div className="text-[13px] text-secondary">{t('timing.score')}</div>
          <div
            className={cx(
              'tnum text-[34px] font-bold leading-tight',
              ahead && 'text-[var(--positive)]',
              behind && 'text-[var(--negative)]',
            )}
          >
            {formatPercent(edgePct)}
          </div>
          <div className="text-[15px] text-secondary">
            {savedBase === null
              ? t('timing.inline')
              : Number(savedBase) >= 0
                ? t('timing.savedAmount', {
                    amount: formatAmount(Math.abs(Number(savedBase)), baseCurrency),
                  })
                : t('timing.lostAmount', {
                    amount: formatAmount(Math.abs(Number(savedBase)), baseCurrency),
                  })}
            <span className="ml-1 text-tertiary">{t('timing.vsMarket')}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Benchmark({
  label,
  value,
  delta,
  emphasis = false,
  onExplain,
  explaining = false,
  explanation,
}: {
  label: string;
  value: string;
  delta?: string | null;
  emphasis?: boolean;
  onExplain?: () => void;
  explaining?: boolean;
  explanation?: string;
}) {
  const deltaValue = delta === null || delta === undefined ? null : Number(delta);

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className={cx('text-[15px]', emphasis ? 'font-semibold' : 'text-secondary')}>
          {label}
        </span>
        {onExplain && (
          <button
            type="button"
            onClick={onExplain}
            aria-expanded={explaining}
            className="press text-tertiary"
            aria-label={label}
          >
            <Info size={14} />
          </button>
        )}
        <span className="flex-1" />
        <span className={cx('tnum text-[19px]', emphasis ? 'font-bold' : 'font-medium')}>
          {value}
        </span>
        {deltaValue !== null && (
          <span
            className={cx(
              'tnum w-16 text-right text-[13px] font-medium',
              deltaValue > 0
                ? 'text-[var(--positive)]'
                : deltaValue < 0
                  ? 'text-[var(--negative)]'
                  : 'text-secondary',
            )}
          >
            {formatPercent(delta)}
          </span>
        )}
      </div>
      {explaining && explanation && (
        <p className="fade-in mt-2 rounded-control bg-[var(--fill-4)] p-3 text-[13px] leading-relaxed text-secondary">
          {explanation}
        </p>
      )}
    </div>
  );
}

function TradeCard({
  kind,
  title,
  occurredOn,
  rate,
  quoteAmount,
  savedBase,
  quoteCurrency,
  baseCurrency,
}: {
  kind: 'best' | 'worst';
  title: string;
  occurredOn: string;
  rate: string;
  quoteAmount: string;
  savedBase: string;
  quoteCurrency: string;
  baseCurrency: string;
}) {
  const { i18n } = useTranslation();
  const positive = kind === 'best';

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={cx(
            'flex size-7 items-center justify-center rounded-full',
            positive
              ? 'bg-[var(--positive)]/15 text-[var(--positive)]'
              : 'bg-[var(--negative)]/15 text-[var(--negative)]',
          )}
        >
          {positive ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
        </span>
        <span className="text-[15px] font-medium">{title}</span>
      </div>

      <div className="tnum mt-3 flex items-baseline gap-2">
        <span className="text-[22px] font-semibold">{formatRate(rate)}</span>
        <span className="text-[13px] text-secondary">
          {formatAmount(quoteAmount, quoteCurrency)} {quoteCurrency}
        </span>
      </div>

      <div className="tnum mt-1 text-[13px] text-secondary">
        {formatFullDate(occurredOn, i18n.language)} ·{' '}
        <span className={positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]'}>
          {Number(savedBase) >= 0 ? '+' : '−'}
          {formatAmount(Math.abs(Number(savedBase)), baseCurrency)}
        </span>
      </div>
    </Card>
  );
}
